import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { WorkOrderLineExtended } from '../components/work-order-line-details/model/work-order-line-extended.model';
import { environment } from '../../../../environments/environment';
import { CamRequest } from '../../asset-management/models/request/CamRequest';
import { WorkOrderLineEditRequest } from 'src/app/shared/services/models/WorkOrderLineEditRequest';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class WorkOrderLineExtendedService {
  private http = inject(HttpClient);

  /**
   * Get extended work order line details from the backend API
   * @param workOrderLineId - The ID of the work order line
   * @param dealerId - The dealer ID (required for API path)
   * @param workOrderHeaderId - The work order header ID (required for API path)
   * @returns Observable<WorkOrderLineExtended>
   */
  getExtendedDetails(workOrderLineId: string, dealerId: string, workOrderHeaderId: string): Observable<WorkOrderLineExtended> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.workOrderLineExtendedDetails
      .replace('{dealerId}', dealerId)
      .replace('{workOrderHeaderId}', workOrderHeaderId)
      .replace('{workOrderLineId}', workOrderLineId)}`;

    return this.http.get<ApiResponse<WorkOrderLineExtended>>(url).pipe(
      map((response) => {
        if (response.data) {
          return response.data;
        } else {
          throw new Error(response.error || 'Failed to fetch extended details');
        }
      })
    );
  }

  getExtendedDetailsWithHeader(workOrderLineId: string, dealerId: string, workOrderHeaderId: string): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.workOrderLineExtendedDetailsWithHeader
      .replace('{dealerId}', dealerId)
      .replace('{workOrderHeaderId}', workOrderHeaderId)
      .replace('{workOrderLineId}', workOrderLineId)}`;

    return this.http.get<ApiResponse<any>>(url).pipe(map((response) => response?.data ?? response));
  }

  verifyDotDetails(dealerId: string, workOrderHeaderId: string, mfgDot: string, rDot: string): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.verifyDotDetails
      .replace('{dealerId}', dealerId)
      .replace('{workOrderHeaderId}', workOrderHeaderId)}`;
    return this.http.get(url, { params: { mfgDot, rDot } });
  }

  getMatchingCamSpecs(dealerId: string, camSpecLines: CamRequest): Observable<any> {
    console.log('Fetching matching cam specs for dealerId:', dealerId, 'with payload:', camSpecLines);
    const url = `${environment.camServiceUrl}${environment.serviceUris.validateCams}`;
    return this.http.post(url, camSpecLines);
  }

  getValidCombination(
    dealerId: string,
    workOrderHeaderId: string,
    workOrderLineId: string,
    validationRequest: WorkOrderLineEditRequest
  ): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.validateCombination
      .replace('{dealerId}', dealerId)
      .replace('{workOrderHeaderId}', workOrderHeaderId)
      .replace('{workOrderLineId}', workOrderLineId)}`;
    return this.http.post<any>(url, validationRequest);
  }
}
